/**
 * Aakruti First-Party Marketing & Retargeting Cache Engine
 * Captures ad click IDs (fbclid, gclid, ttclid), UTM campaign parameters,
 * and browsed products into first-party storage to maximize retargeting audience retention.
 */
(function () {
  'use strict';

  const STORAGE_KEY_ATTRIBUTION = 'aakruti_marketing_cache';
  const STORAGE_KEY_VIEWED = 'aakruti_viewed_cache';
  const COOKIE_NAME_ATTRIBUTION = '_aakruti_attribution';
  const COOKIE_NAME_CONSENT = 'aakruti_cookie_consent';
  const ATTRIBUTION_EXPIRY_DAYS = 30;

  // Helper: Get Cookie
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return match ? decodeURIComponent(match[3]) : null;
  }

  // Helper: Set 1st-Party Cookie
  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax; Secure`;
  }

  // Helper: Sync with Shopify Customer Privacy API
  function syncShopifyPrivacyConsent(granted) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      try {
        if (typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
          window.Shopify.customerPrivacy.setTrackingConsent(granted, function () {
            console.log('[Aakruti Retargeting] Shopify tracking consent synced:', granted);
          });
        }
      } catch (err) {
        console.warn('[Aakruti Retargeting] Error syncing Shopify privacy:', err);
      }
    }
  }

  // 1. CAPTURE & CACHE AD CLICK IDS AND UTM PARAMETERS
  function captureAttribution() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const trackingKeys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'fbclid',
        'gclid',
        'ttclid',
        'sc_clickid',
        'msclkid'
      ];

      const captured = {};
      let hasTrackingParams = false;

      trackingKeys.forEach((key) => {
        if (urlParams.has(key)) {
          captured[key] = urlParams.get(key);
          hasTrackingParams = true;
        }
      });

      // Load existing cache if present
      let existing = {};
      try {
        const raw = localStorage.getItem(STORAGE_KEY_ATTRIBUTION);
        if (raw) existing = JSON.parse(raw);
      } catch (e) {}

      if (hasTrackingParams) {
        // Merge with existing, keeping first-touch if preferred or updating latest campaign
        const updated = {
          ...existing,
          ...captured,
          last_touch: new Date().toISOString(),
          landing_url: window.location.href.split('?')[0]
        };

        if (!updated.first_touch) {
          updated.first_touch = updated.last_touch;
        }

        // Save to localStorage & 1st-party cookie (survives cross-session & Safari ITP)
        const serialized = JSON.stringify(updated);
        localStorage.setItem(STORAGE_KEY_ATTRIBUTION, serialized);
        setCookie(COOKIE_NAME_ATTRIBUTION, serialized, ATTRIBUTION_EXPIRY_DAYS);

        // Expose globally for ad tags / pixels
        window.__aakrutiAttribution = updated;

        // Sync with cart attributes for order conversion tracking
        syncCartAttributes(updated);
      } else if (existing && Object.keys(existing).length > 0) {
        window.__aakrutiAttribution = existing;
      }
    } catch (err) {
      console.warn('[Aakruti Retargeting] Attribution capture error:', err);
    }
  }

  // 2. ATTACH ATTRIBUTION TO CART FOR ORDER CONVERSION TRACKING
  function syncCartAttributes(attribution) {
    try {
      const attributes = {};
      if (attribution.utm_source) attributes['_utm_source'] = attribution.utm_source;
      if (attribution.utm_campaign) attributes['_utm_campaign'] = attribution.utm_campaign;
      if (attribution.utm_medium) attributes['_utm_medium'] = attribution.utm_medium;
      if (attribution.fbclid) attributes['_fbclid'] = attribution.fbclid;
      if (attribution.gclid) attributes['_gclid'] = attribution.gclid;

      if (Object.keys(attributes).length === 0) return;

      const updateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
      fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      }).catch(() => {});
    } catch (e) {}
  }

  // 3. CAPTURE RECENTLY VIEWED PRODUCTS IN BROWSER CACHE
  function captureViewedProduct() {
    try {
      if (!window.__aakrutiProductData) return;
      const product = window.__aakrutiProductData;

      let viewed = [];
      try {
        const raw = localStorage.getItem(STORAGE_KEY_VIEWED);
        if (raw) viewed = JSON.parse(raw);
      } catch (e) {}

      // Remove duplicates
      viewed = viewed.filter((item) => item.id !== product.id);

      // Prepend current product with timestamp
      viewed.unshift({
        ...product,
        viewed_at: new Date().toISOString()
      });

      // Keep max 10 items in cache
      if (viewed.length > 10) viewed = viewed.slice(0, 10);

      localStorage.setItem(STORAGE_KEY_VIEWED, JSON.stringify(viewed));
      window.__aakrutiViewedProducts = viewed;
    } catch (err) {
      console.warn('[Aakruti Retargeting] Viewed product cache error:', err);
    }
  }

  // 4. CONSENT LISTENER & INITIALIZATION
  function init() {
    captureAttribution();
    captureViewedProduct();

    // Check existing consent
    const consent = localStorage.getItem(COOKIE_NAME_CONSENT);
    if (consent === 'accepted') {
      syncShopifyPrivacyConsent(true);
    }

    // Expose retargeting helper API
    window.AakrutiRetargeting = {
      getAttribution: function () {
        try {
          return JSON.parse(localStorage.getItem(STORAGE_KEY_ATTRIBUTION) || '{}');
        } catch (e) {
          return {};
        }
      },
      getViewedProducts: function () {
        try {
          return JSON.parse(localStorage.getItem(STORAGE_KEY_VIEWED) || '[]');
        } catch (e) {
          return [];
        }
      },
      setConsent: function (granted) {
        localStorage.setItem(COOKIE_NAME_CONSENT, granted ? 'accepted' : 'declined');
        setCookie(COOKIE_NAME_CONSENT, granted ? 'accepted' : 'declined', 365);
        syncShopifyPrivacyConsent(granted);
        document.dispatchEvent(new CustomEvent('aakruti:consent-updated', { detail: { granted } }));
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
